import { GeoJsonLayer } from '@deck.gl/layers';
import proj4 from 'proj4';
import * as shapefile from 'shapefile';
import JSZip from 'jszip';
import bbox from '@turf/bbox';
import { showMapConfirmToast } from "../utils/Toast/mapToast";

const transformCoordinates = (coords, sourceCRS, targetCRS) => {
  return coords.map(coord => {
    try {
      const [x, y] = coord;
      const [lon, lat] = proj4(sourceCRS, targetCRS, [x, y]);
      return [lon, lat];
    } catch (error) {
      console.error('Error transforming coordinates:', error);
      return coord;
    }
  });
};

const transformGeoJSONCoordinates = (geometry, sourceCRS, targetCRS) => {
  const { type, coordinates } = geometry;

  switch (type) {
    case 'Point':
      return {
        ...geometry,
        coordinates: transformCoordinates([coordinates], sourceCRS, targetCRS)[0],
      };

    case 'LineString':
      return {
        ...geometry,
        coordinates: transformCoordinates(coordinates, sourceCRS, targetCRS),
      };

    case 'Polygon':
      return {
        ...geometry,
        coordinates: coordinates.map(ring =>
          transformCoordinates(ring, sourceCRS, targetCRS)
        ),
      };

    case 'MultiPoint':
      return {
        ...geometry,
        coordinates: transformCoordinates(coordinates, sourceCRS, targetCRS),
      };

    case 'MultiLineString':
      return {
        ...geometry,
        coordinates: coordinates.map(line =>
          transformCoordinates(line, sourceCRS, targetCRS)
        ),
      };

    case 'MultiPolygon':
      return {
        ...geometry,
        coordinates: coordinates.map(polygon =>
          polygon.map(ring => transformCoordinates(ring, sourceCRS, targetCRS))
        ),
      };

    default:
      console.error('Unsupported geometry type:', type);
      return geometry;
  }
};

const transformGeoJSON = (geojson, sourceCRS, targetCRS = 'EPSG:4326') => {
  return {
    ...geojson,
    features: geojson.features.map(feature => ({
      ...feature,
      geometry: transformGeoJSONCoordinates(feature.geometry, sourceCRS, targetCRS),
    })),
  };
};

const extractFilesFromZip = async (zipFile) => {
  try {
    const zip = new JSZip();
    const content = await zip.loadAsync(zipFile);
    const files = {};

    await Promise.all(
      Object.keys(content.files).map(async (filename) => {
        files[filename] = await zip.file(filename).async('blob');
      })
    );

    return files;
  } catch (error) {
    console.error('Error extracting .zip file:', error);
    throw error;
  }
};

const parsePrjFile = async (prjBlob) => {
  const prjText = await prjBlob.text();
  return proj4(prjText);
};

const parseDbfBlob = async (dbfBlob) => {
  const arrayBuffer = await dbfBlob.arrayBuffer();
  const dataView = new DataView(arrayBuffer);

  const headerLength = dataView.getUint16(8, true);
  const recordLength = dataView.getUint16(10, true);
  const numberOfRecords = dataView.getUint32(4, true);

  const fields = [];
  let offset = 32;

  while (dataView.getUint8(offset) !== 0x0D) {
    const fieldName = new TextDecoder()
      .decode(new Uint8Array(arrayBuffer, offset, 10))
      .replace(/\0/g, '');

    const fieldType = String.fromCharCode(dataView.getUint8(offset + 11));
    const fieldLength = dataView.getUint8(offset + 16);

    fields.push({ fieldName, fieldType, fieldLength });
    offset += 32;
  }

  const records = [];
  offset = headerLength;

  for (let i = 0; i < numberOfRecords; i++) {
    const record = {};

    fields.reduce((recordOffset, field) => {
      const rawValue = new TextDecoder()
        .decode(new Uint8Array(arrayBuffer, offset + recordOffset, field.fieldLength))
        .trim();

      record[field.fieldName] =
        field.fieldType === 'N' ? parseFloat(rawValue) : rawValue;

      return recordOffset + field.fieldLength;
    }, 1);

    offset += recordLength;
    records.push(record);
  }

  return records;
};

const handleZipFile = async (zipFile) => {
  try {
    const files = await extractFilesFromZip(zipFile);

    const shpBlob = files[Object.keys(files).find(name => name.endsWith('.shp'))];
    const prjBlob = files[Object.keys(files).find(name => name.endsWith('.prj'))];
    const dbfBlob = files[Object.keys(files).find(name => name.endsWith('.dbf'))];

    if (!shpBlob) throw new Error('.shp file not found in .zip');
    if (!dbfBlob) throw new Error('.dbf file not found in .zip');

    const dbfRecords = await parseDbfBlob(dbfBlob);

    let sourceCRS = 'EPSG:4326';

    if (prjBlob) {
      sourceCRS = await parsePrjFile(prjBlob);
    }

    const arrayBuffer = await shpBlob.arrayBuffer();
    const shpReader = await shapefile.open(arrayBuffer);

    const features = [];
    let result;
    let dbfIndex = 0;

    while (!(result = await shpReader.read()).done) {
      const feature = result.value;
      const properties = { ...dbfRecords[dbfIndex] };

      features.push({
        ...feature,
        properties,
      });

      dbfIndex++;
    }

    const geojson = {
      type: 'FeatureCollection',
      features,
    };

    return transformGeoJSON(geojson, sourceCRS, 'EPSG:4326');
  } catch (error) {
    console.error('Error handling .zip file:', error);
    throw error;
  }
};

export const loadShapefile = async (file) => {
  try {
    if (file.name.endsWith('.zip')) {
      return await handleZipFile(file);
    }

    throw new Error('Unsupported file type. Please upload a .zip file.');
  } catch (error) {
    console.error('Error loading shapefile:', error);
    throw error;
  }
};

export const setShapefileLayer = (
  layerIndex,
  shapefile,
  setHoverInfo,
  setWfsLayers,
  setViewState
) => {
  setWfsLayers(prev => [
    ...prev,
    new GeoJsonLayer({
      id: `shp-${layerIndex}`,
      data: shapefile,
      source: "catalog",
      opacity: 1,
      pickable: true,
      stroked: true,
      filled: true,
      visible: true,
      pointType: 'circle',
      lineWidthMinPixels: 2,
      lineWidthScale: 2,
      getFillColor: [255, 0, 0, 100],
      getLineColor: [255, 0, 0, 200],
      getPointRadius: 200,
      getLineWidth: 2,
      onHover: info => setHoverInfo(info),
      onError: (error) => console.error('GeoJsonLayer error:', error),
    }),
  ]);

  if (shapefile && shapefile.features && shapefile.features.length > 0) {
    const [minX, minY, maxX, maxY] = bbox(shapefile);
    const centerLon = (minX + maxX) / 2;
    const centerLat = (minY + maxY) / 2;
    const lonRange = maxX - minX;
    const latRange = maxY - minY;
    const zoom = Math.log2(360 / Math.max(lonRange, latRange));

    showMapConfirmToast({
      title: "Pozicioniranje mape",
      message: "Da li želite da se prikaz mape pozicionira prema učitanom SHP sloju?",
      confirmText: "Da",
      cancelText: "Ne",
      variant: "info",
      onConfirm: () => {
        setViewState(prev => ({
          ...prev,
          longitude: centerLon,
          latitude: centerLat,
          zoom,
          pitch: 0,
          bearing: 0,
          transitionDuration: 1000,
        }));
      },
    });
  }
};